import { useEffect, useRef } from "react";

import { useEventCallback } from "usehooks-ts";

import { useMutationObserver } from "./useMutationObserver";

import type { RefObject } from "react";

type Tracked = {
  item: HTMLElement;
  index: number;
  preceding: HTMLElement[];
  following: HTMLElement[];
};

function hasOpenPopup(container: HTMLElement) {
  return !!container.querySelector('[aria-haspopup][aria-expanded="true"]');
}

function focusIsLost() {
  return !document.activeElement || document.activeElement === document.body;
}

/**
 * Keeps focus in `containerRef` when the item holding focus is removed, such as a deleted row or a
 * "Show more" row replaced by the rows it reveals: focus moves to the first item added in its place,
 * else to the next item after it that is still there, else to the item now at its index, else to the
 * last item. `focusSelector` picks what to focus
 * inside an item that isn't focusable itself.
 *
 * Applies only while focus was in the container or in a popup opened from it (portaled menus
 * included), and only if focus fell back to the page, so a removal never takes focus from elsewhere.
 */
export function useFocusRestoration(
  containerRef: RefObject<HTMLElement | null>,
  itemSelector = "button:not(:disabled)",
  focusSelector = itemSelector,
) {
  const trackedRef = useRef<Tracked | null>(null);
  const frameRef = useRef(0);

  const itemsOf = (container: HTMLElement) =>
    Array.from(container.querySelectorAll<HTMLElement>(itemSelector));

  // The items around the focused one are kept too: a removal can take out the focused item along with
  // a duplicate of it, and the next item in the list is what is left to focus
  const track = (items: HTMLElement[], index: number) => {
    trackedRef.current =
      index === -1
        ? null
        : {
            item: items[index],
            index,
            preceding: items.slice(0, index),
            following: items.slice(index + 1),
          };
  };

  const nextFocusOf = ({ index, preceding, following }: Tracked, items: HTMLElement[]) => {
    const before = preceding.findLast((item) => item.isConnected);
    const after = following.find((item) => item.isConnected);
    // Whatever sits between the neighbours still there was added in the removed item's place
    const start = before ? items.indexOf(before) + 1 : 0;
    const end = after ? items.indexOf(after) : items.length;
    return items.slice(start, end)[0] ?? after ?? items[Math.min(index, items.length - 1)];
  };

  useMutationObserver(
    containerRef,
    (container) => {
      const tracked = trackedRef.current;
      if (!tracked) return;
      if (tracked.item.isConnected) {
        const items = itemsOf(container);
        track(items, items.indexOf(tracked.item));
        return;
      }
      trackedRef.current = null;
      cancelAnimationFrame(frameRef.current);
      // A closing menu hands focus back to its button first; wait until that has settled
      frameRef.current = requestAnimationFrame(() => {
        if (!focusIsLost()) return;
        const next = nextFocusOf(tracked, itemsOf(container));
        (next?.matches(focusSelector)
          ? next
          : next?.querySelector<HTMLElement>(focusSelector)
        )?.focus();
      });
    },
    { childList: true, subtree: true },
  );

  const onFocusIn = useEventCallback((event: FocusEvent) => {
    const container = containerRef.current;
    const target = event.target as Node;
    if (!container) return;
    if (container.contains(target)) {
      const items = itemsOf(container);
      track(
        items,
        items.findLastIndex((item) => item.contains(target)),
      );
    } else if (!hasOpenPopup(container)) {
      trackedRef.current = null;
    }
  });

  // A click on a blank part of the page blurs to nothing, unlike a removal, which leaves the
  // element detached
  const onFocusOut = useEventCallback((event: FocusEvent) => {
    const target = event.target as Element;
    if (event.relatedTarget || !trackedRef.current?.item.contains(target)) return;
    requestAnimationFrame(() => {
      if (target.isConnected && focusIsLost()) trackedRef.current = null;
    });
  });

  useEffect(() => {
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      cancelAnimationFrame(frameRef.current);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, [onFocusIn, onFocusOut]);
}
